/**
 * Words for the search screens (F13): source names, execution statuses as
 * text with a shape (never colour alone, DES-045), failure reasons and the
 * line comparison of a draft and its edit (SRCH-161). No model or vendor name
 * appears anywhere on these screens (CONT-058).
 */
import type { ApiSearchSource, ExecutionKind, ExecutionStatus, SearchExecution, SearchSource } from '@bioterafi/api-contract';

export const SOURCE_LABEL: Readonly<Record<SearchSource, string>> = {
  pubmed: 'PubMed',
  europepmc: 'Europe PMC',
  ctgov: 'ClinicalTrials.gov',
  openalex: 'OpenAlex',
  upload: 'Uploaded results',
};

export const API_SOURCES: readonly ApiSearchSource[] = ['pubmed', 'europepmc', 'ctgov', 'openalex'];

export const KIND_LABEL: Readonly<Record<ExecutionKind, string>> = {
  search: 'Database or register search',
  upload: 'Uploaded export',
  citation_backward: 'Reference lists',
  citation_forward: 'Citing works',
};

/** Execution error codes in plain words (F13 design, error codes). */
const ERROR_WORDS: Readonly<Record<string, string>> = {
  source_not_configured: 'not configured',
  source_unavailable: 'source unavailable',
  source_rejected_request: 'the source refused the request',
  hit_limit_exceeded: 'too many hits to retrieve',
  count_mismatch: 'fewer records than hits',
  pagination_loop: 'paging stopped early',
  citation_cap_reached: 'more than 2 000 items for a seed',
  response_too_large: 'response too large',
  unexpected_content_type: 'unexpected response',
  insecure_redirect: 'insecure redirect refused',
  daily_limit_reached: 'daily limit reached',
};

export function errorWords(code: string | null): string {
  if (!code) return 'unknown problem';
  return ERROR_WORDS[code] ?? code.replace(/_/g, ' ');
}

export interface StatusLabel {
  text: string;
  mark: string;
  /** Failed and partial cells carry a pattern as well as the words (DES-045). */
  pattern: 'none' | 'hatched' | 'dotted';
}

/** SRCH-162: "Failed: source unavailable", "Partial: paging stopped early", "Completed". */
export function executionStatus(e: Pick<SearchExecution, 'status' | 'errorCode'>): StatusLabel {
  const status: ExecutionStatus = e.status;
  switch (status) {
    case 'ok':
      return { text: 'Completed', mark: '●', pattern: 'none' };
    case 'running':
      return { text: 'Running', mark: '◐', pattern: 'none' };
    case 'partial':
      return { text: `Partial: ${errorWords(e.errorCode)}`, mark: '◑', pattern: 'dotted' };
    case 'failed':
      return { text: `Failed: ${errorWords(e.errorCode)}`, mark: '✕', pattern: 'hatched' };
  }
}

export function countCell(value: number | null, status: ExecutionStatus): string {
  if (status === 'failed' && value === null) return 'Not searched';
  return value === null ? 'Not recorded' : value.toLocaleString('en-GB');
}

export type DiffLine = { kind: 'same' | 'added' | 'removed'; text: string };

/** SRCH-161: line-by-line comparison (longest common subsequence). */
export function lineDiff(before: string, after: string): DiffLine[] {
  const a = before.split(/\r?\n/);
  const b = after.split(/\r?\n/);
  const table: number[][] = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      table[i]![j] = a[i] === b[j] ? table[i + 1]![j + 1]! + 1 : Math.max(table[i + 1]![j]!, table[i]![j + 1]!);
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      out.push({ kind: 'same', text: a[i]! });
      i += 1;
      j += 1;
    } else if (table[i + 1]![j]! >= table[i]![j + 1]!) {
      out.push({ kind: 'removed', text: a[i]! });
      i += 1;
    } else {
      out.push({ kind: 'added', text: b[j]! });
      j += 1;
    }
  }
  while (i < a.length) out.push({ kind: 'removed', text: a[i++]! });
  while (j < b.length) out.push({ kind: 'added', text: b[j++]! });
  return out;
}

/** Filters each source accepts (F13 design, Strategy filters), with their labels. */
export const FILTER_FIELDS: Readonly<Record<ApiSearchSource, readonly { key: string; label: string; hint: string; type: 'date' | 'text' }[]>> = {
  pubmed: [
    { key: 'dateFrom', label: 'Published from', hint: 'Optional. YYYY-MM-DD.', type: 'date' },
    { key: 'dateTo', label: 'Published to', hint: 'Optional. YYYY-MM-DD.', type: 'date' },
  ],
  europepmc: [
    { key: 'dateFrom', label: 'Published from', hint: 'Optional. YYYY-MM-DD.', type: 'date' },
    { key: 'dateTo', label: 'Published to', hint: 'Optional. YYYY-MM-DD.', type: 'date' },
  ],
  ctgov: [
    { key: 'query.cond', label: 'Condition', hint: 'Optional, plain words.', type: 'text' },
    { key: 'query.intr', label: 'Intervention', hint: 'Optional, plain words.', type: 'text' },
    { key: 'query.outc', label: 'Outcome', hint: 'Optional, plain words.', type: 'text' },
    { key: 'filter.advanced', label: 'Advanced filter', hint: 'Optional Essie expression, for example AREA[DesignAllocation]RANDOMIZED.', type: 'text' },
  ],
  openalex: [{ key: 'filter', label: 'Filter', hint: 'Optional key:value pairs, for example publication_year:2020.', type: 'text' }],
};
